// Opens a workspace's landing page when its person OPENS Stride — the shell
// side of utils/workspaceLanding.js, which holds the whole rule. Draws
// nothing. Mounted once in the shell (web/src/App.jsx ProtectedLayout).
//
// Waits for the person's groups and the workspaces definition, takes the
// once-per-page-load opening record (utils/landing.js takeAppEntry), and —
// only when the rule says so — brings the landing page to the front: its
// tab if one is already open, else in place of the Home tab the opening
// showed, else as a new tab (the person's other tabs are kept). Home stays
// one click away in the bar.

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@platform/auth/frontend/contexts/AuthContext';
import { useTab, isOutsideTabs } from '@platform/app-shell/frontend/contexts/TabContext';
import useWorkspaces from '@platform/app-shell/frontend/components/TopNav/useWorkspaces';
import { WORKSPACES_ENABLED } from '@platform/app-shell/frontend/utils/workspacesEnabled';
import { HOME_URL, takeAppEntry } from '@platform/app-shell/frontend/utils/landing';
import { workspaceLanding, detectTouch } from '@platform/app-shell/frontend/utils/workspaceLanding';

const pathOf = (url) => String(url || '').split(/[?#]/)[0];

export default function WorkspaceLanding() {
  const { userGroups } = useAuth();
  const { loaded, active } = useWorkspaces();
  const { tabs, activeTabId, switchTab, replaceTab, openTab, isPopoutWindow } = useTab();
  const navigate = useNavigate();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    if (WORKSPACES_ENABLED && (!loaded || userGroups == null)) return; // not known yet
    done.current = true;
    const entry = takeAppEntry();
    if (isPopoutWindow) return;
    const target = workspaceLanding({
      entryPath: entry.entry ? entry.path : null,
      deepLink: entry.deepLink,
      active,
      touch: detectTouch(),
      enabled: WORKSPACES_ENABLED,
    });
    if (!target) return;
    if (isOutsideTabs(target)) { navigate(target, { replace: true }); return; }
    const open = tabs.find((t) => pathOf(t.url) === pathOf(target));
    if (open) { switchTab(open.id); return; }
    const current = tabs.find((t) => t.id === activeTabId);
    const onHome = current && (pathOf(current.url) === HOME_URL || pathOf(current.url) === '/');
    if (onHome) replaceTab(current.id, target);
    else openTab(target);
  }, [loaded, active, userGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
